import { useRtkQueryDynamicEndpoint } from "../../../common/hooks/useRtkQueryDynamicEndpoint";
import {
	ContractPeriod,
	FieldDataDefQuery,
} from "../../../requests_cm/gecoReferentialService/types";
import {
	useLazyGetTimeseriesDataQuery,
	useLazyGetTimeseriesMetadataQuery,
} from "../../../requests_cm/gecoTimeserieService/service";
import {
	ContractDef,
	FieldDataQueryDef,
	FieldDataQueryResult,
	GetTimeserieMetadata,
	Point,
	TimeserieDataResp,
	TimeserieMetadataResp,
} from "../../../requests_cm/gecoTimeserieService/types";
import { QuerySetpForm } from "../TimeseriesPage";
import { removeWhitespace } from "../TimeseriesPage.helper";
import { groupBy, map, sumBy } from "lodash";

export function useGetTimeseriesDataFromGeco() {
	const [getTimeseriesMetadata] = useLazyGetTimeseriesMetadataQuery();
	const getTsMetadata = useRtkQueryDynamicEndpoint(getTimeseriesMetadata);
	const [getTimeseriesData] = useLazyGetTimeseriesDataQuery();

	const getTsdata = useRtkQueryDynamicEndpoint(getTimeseriesData);

	const getMetadata = async (
		arr: (FieldDataDefQuery & ContractPeriod)[],
		formValues: QuerySetpForm
	) => {
		const metadataPromises = arr.map(
			(el: FieldDataDefQuery & ContractPeriod) => {
				return getTsMetadata(
					{
						resourceType: "contract_period",
						resourceId: el.id,
						fielddata_id: el.fielddata_id,
						fielddata_name: el.query_name,
						fielddata_subquery: el.sub_query,
						kind: el.fielddata_kind.name,
						type: el.fielddata_type.name,
						variant: el.fielddata_variant.name,
						maturity: el.fielddata_maturity.name,
						startDate: el.start_date,
						endDate: el.end_date,
						unit: el.unit,
						step: el.step,
						contractId: el.contractId,
						contractName: el.contractName
					},
					false
				);
			}
		);

		const metadatResponses = await Promise.all(metadataPromises);
		let listOfMetaDataTs: (TimeserieMetadataResp & GetTimeserieMetadata)[] =
			[];

		// Store `unit` and `step` from the original request args, as they are request-specific.
		metadatResponses.forEach(({ data, originalArgs }) => {
			if (data) {
				const mergedData = data.map((item) => ({
					...item,
					...originalArgs,
				}));
				listOfMetaDataTs = [...listOfMetaDataTs, ...mergedData];
			}
		});

		const timeseriesDataPromises = listOfMetaDataTs.map(
			(el: TimeserieMetadataResp & GetTimeserieMetadata) =>
				getTsdata(
					{
						timeserieId: el.id.toString(),
						contractId: el.contractId,
						contractName: el.contractName,
						fielddata_id: el.fielddata_id,
						fielddata_name: el.fielddata_name,
						fielddata_subquery: el.fielddata_subquery,
						unit: el.unit ?? "",
						simplingRate: removeWhitespace(el.step ?? ""),
						resourceType: el.resourceType,
						resourceId: el.resourceId,
						startDate:
							formValues.dateRange.startDate
								?.toISOString()
								.split("T")[0] ?? "",
						endDate:
							formValues.dateRange.endDate
								?.toISOString()
								.split("T")[0] ?? "",
					},
					false
				)
		);

		const timeseriesDataResponses = await Promise.allSettled(
			timeseriesDataPromises
		);
		const successfulResults = timeseriesDataResponses
			.filter(
				(result) => result.status === "fulfilled" && result.value.isSuccess
			)
			// @ts-ignore
			.map((result) => result.value);

		console.log(successfulResults);

		const distinctContracts = arr.reduce((prev, el): ContractDef[] => {
			if(prev.some((co: ContractDef) => co.contractId == el.contractId && co.contractName == el.contractName))
				return prev;
			return [...prev, {contractId: el.contractId, contractName: el.contractName}]
		}, []);

		const distinctQueries = arr.reduce((prev, el): FieldDataQueryDef[] =>{
			if(prev.some((fd: FieldDataQueryDef) => fd.queryName == el.query_name && fd.subQueryName == el.sub_query && fd.unit == el.unit))
				return prev;
			return [...prev, {queryName: el.query_name, subQueryName: el.sub_query, unit: el.unit}]
		}, []);

		const byQuery = distinctQueries.map((fd: FieldDataQueryDef): FieldDataQueryResult[] => {
			return distinctContracts.map((contract: ContractDef) => {
				const contractTimeseries = successfulResults.filter(p => 
					p.originalArgs?.fielddata_name === fd.queryName && p.originalArgs?.fielddata_subquery == fd.subQueryName &&
					p.originalArgs?.contractId === contract.contractId);
				const uniqueTimeSeries = new Set<string>();  
				const normalizedTimeseries: Point[] = contractTimeseries.map(m => {
						// Merge (sum) timeseries around the contract id 
						// Make it arrays of objects (instead of array of array for further grouping)
						return m.data.flatMap((ts: TimeserieDataResp) => {
							const timeserieName = Object.keys(ts)[0];
							// Don't count a timeserie twice per contract
							if (uniqueTimeSeries.has(timeserieName)) {
								return [];
							}
							uniqueTimeSeries.add(timeserieName);
							return ts[timeserieName].map(t => ({ timeStamp: t[0], value: t[1] }));
					});
				}).flat();

				const joinedTs: Point[] = [...normalizedTimeseries].flat();

				const groupedByTimeStamps = groupBy(joinedTs, "timeStamp");
				const mergedResult = map(groupedByTimeStamps, (objs: number[], key: number) => ({
					"timeStamp": key,
					"value": sumBy<number>(objs, "value")
				}));
				return { unit: fd?.unit, queryName: fd.queryName, subQueryName: fd.subQueryName, contractId: contract.contractId, contractName: contract.contractName, data: mergedResult };
			});
		});

		const failedResults = timeseriesDataResponses
			.filter(
				(result): result is PromiseFulfilledResult<any> =>
					result.status === "fulfilled" && result.value.isError
			)
			.map((result) => result.value.error);

		console.debug("Failed Request  :", failedResults.length);
		console.debug("Successful Request  :", successfulResults.length);

		return byQuery.flat();
	};
	return getMetadata;
}
